import React, { Suspense, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, useHistory, useLocation } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import LoaderOverlay from 'components/LoaderOverlay';
import { RouteDefinition } from 'navigation/types';
import styled from 'styled-components';
import LogoutModal from '../../components/LogoutModal';
import GuardedRoute from '../GuardedRoute';

import {
  Navbar,
  NavbarMobile,
  Sidebar,
  SidebarMenuListItem,
  SidebarMobile,
} from 'components/common';
import NavbarLanguageSelector from 'components/NavbarLanguageSelector';

import HomeIcon from 'assets/images/sidebaricons/homeicon.svg';
import LogoutIcon from 'assets/images/sidebaricons/logouticon.svg';
import DocumentIcon from 'assets/images/sidebaricons/documenticon.svg';
import RequestIcon from 'assets/images/sidebaricons/requesticon.svg';

import HomeIconDark from 'assets/images/sidebaricons/homeicondark.svg';
import LogoutIconDark from 'assets/images/sidebaricons/logouticondark.svg';
import DocumentIconDark from 'assets/images/sidebaricons/documenticondark.svg';
import RequestIconDark from 'assets/images/sidebaricons/requesticondark.svg';

import HomeIconActive from 'assets/images/sidebaricons/homeiconactive.svg';
import LogoutIconActive from 'assets/images/sidebaricons/logouticonactive.svg';
import DocumentIconActive from 'assets/images/sidebaricons/documenticonactive.svg';
import RequestIconActive from 'assets/images/sidebaricons/requesticonactive.svg';

import { privateroutes } from 'navigation/privateroutes';
import { history } from '../../store/store';
import { getUserIfToken, updateUserAccount, useInterval } from '../../util';

import { checkUserStep } from 'util/helpers';

import './style.scss';
import ManagedBar from 'components/ManagedBar';
import userAvatar from 'assets/images/user-avatar.jpg';

type AppMainProps = {
  routes: Array<RouteDefinition>;
  getDefaultRoute: (routes: Array<RouteDefinition>) => void;
};

const defaultSidebarMenu: Array<SidebarMenuListItem> = [
  {
    name: 'dashboard',
    path: '/',
    icon: HomeIcon,
    iconactive: HomeIconActive,
    icondark: HomeIconDark,
    clickAction: () => history.push('/'),
    isActive: false,
  },
  {
    name: 'request',
    path: '/request',
    icon: RequestIcon,
    iconactive: RequestIconActive,
    icondark: RequestIconDark,
    clickAction: () => history.push('/request'),
    isActive: false,
  },
  {
    name: 'documents',
    path: '/documents',
    icon: DocumentIcon,
    iconactive: DocumentIconActive,
    icondark: DocumentIconDark,
    clickAction: () => history.push('/documents'),
    isActive: false,
  },
];

const mobileLogout = {
  icon: LogoutIcon,
  iconactive: LogoutIconActive,
  icondark: LogoutIconDark,
  name: 'Logout',
  clickAction: () => {
    //@ts-ignore
    document.getElementById('logout-btn').click();
  },
  isActive: false,
};

const DashboardLayout: React.FC<AppMainProps> = ({ routes, getDefaultRoute }) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { isAuthenticated, token, user, userCore } = useSelector((state: any) => state.authReducer);

  const [userHasToken, setUserHasToken] = useState<boolean | null>(null);
  const [userName, setUserName] = useState<string>('');
  const [authStateIsLoaded, setAuthStateIsLoaded] = useState<boolean>(false);
  const [sidebarMenu, setSidebarMenu] = useState<Array<SidebarMenuListItem>>(defaultSidebarMenu);
  const [mobileSideBarVisible, setMobileSideBarVisible] = useState<boolean>(false);

  const getUserData = () => {
    if (isAuthenticated && token) {
      updateUserAccount();
    }
  };

  useInterval(() => {
    getUserData();
  }, 3000);

  useEffect(() => {
    getUserIfToken().then((res) => {
      if (!res) {
        setUserHasToken(false);
        history.push('/login');
      } else setUserHasToken(true);
    });
    return () => {};
  }, []);

  useEffect(() => {
    if (userHasToken !== null) {
      if (!userHasToken || !isAuthenticated || !token) {
        history.push('/login');
      } else {
        if (user.firstName && user.lastName) {
          setUserName(`${user.firstName} ${user.lastName}`);
        }
        checkUserStep(user, userCore, history);
        setAuthStateIsLoaded(true);
      }
    }
  }, [userHasToken]);

  const updateActiveMenuItem = (activeParent: string) => {
    const localArray = [...defaultSidebarMenu];
    let objForEditIndex = localArray.findIndex((obj) => obj.name == activeParent);
    localArray.map((item) => {
      item.isActive = false;
      return item;
    });
    localArray[objForEditIndex].isActive = true;
    setSidebarMenu(localArray);
  };

  const getActiveRoute = () => {
    let currentRoute = privateroutes.find((item) => item.path == location.pathname);
    if (currentRoute && currentRoute.parent) {
      updateActiveMenuItem(currentRoute.parent);
    }
  };

  useEffect(() => {
    if (sidebarMenu.length > 0) {
      getActiveRoute();
    }
    setMobileSideBarVisible(false);
  }, [location]);

  const getSidebarMobileMenu = useMemo(() => [...sidebarMenu, mobileLogout], [sidebarMenu]);

  if (authStateIsLoaded) {
    return (
      <>
        <Suspense fallback={<LoaderOverlay />}>
          <div className={'dashboard-main-layout'}>
            <ManagedBar userCore={userCore} />
            <div className={'d-md-none'}>
              <NavbarMobile
                userName={userName}
                userAvatar={userAvatar}
                status={userCore?.status}
                onPressUser={() => history.push('/user/details')}
                isPressed={mobileSideBarVisible}
                onPressBurger={() => setMobileSideBarVisible(true)}
                onPressClose={() => setMobileSideBarVisible(false)}
              />
            </div>
            <div className={'d-none d-md-block'}>
              <Navbar
                userName={userName}
                onPressUser={() => history.push('/user/details')}
                userAvatar={userAvatar}
                status={userCore?.status}
                languageSelectorComponent={<NavbarLanguageSelector />}
              />
            </div>
            <div className={'dashboard-page-content'}>
              <div className={'dashboard-routes-content-layout custom-scroll'}>
                <TransitionGroup>
                  <CSSTransition
                    key={location.key}
                    classNames="fade"
                    timeout={50}
                  >
                    <Switch>
                      {routes.map((route: any) => {
                        return (
                          <GuardedRoute
                            canAccess={true}
                            key={route.name}
                            {...route}
                          />
                        );
                      })}
                      {getDefaultRoute(routes || [])}
                    </Switch>
                  </CSSTransition>
                </TransitionGroup>
              </div>
              <div className={'d-none d-md-block'}>
                <Sidebar menu={sidebarMenu} />
              </div>
              <div className={'d-md-none'}>
                <SidebarMobile
                  isVisible={mobileSideBarVisible}
                  menu={getSidebarMobileMenu}
                />
              </div>
            </div>
          </div>
        </Suspense>
        <LogoutModal />
      </>
    );
  } else return null;
};

export default DashboardLayout;
